import React, { useEffect, useMemo } from 'react';

import { IndexPageBase, WebsiteImage } from '@flipdish/api-client-typescript';
import { makeStyles } from '@material-ui/core';
import { FormikProps, withFormik } from 'formik';
import { debounce, isEqual } from 'lodash';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import PageLayout from '@fd/ui/Layout';

import { processImages } from '../../../components/WebsiteAndApp/Edit/EditWebsiteSettings';
import useIsMounted from '../../../custom-hooks/useIsMounted';
import useRequestSnackBar, {
  useRequestSnackBarIndicators,
} from '../../../custom-hooks/useRequestSnackBar';
import { getSelectedApp } from '../../../selectors/app.selector';
import {
  createLoadingErrorSelectorFactory,
  createLoadingSelector,
} from '../../../selectors/loading.selector';
import {
  ADD_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_IMAGE,
  DELETE_WEBSITE_SETTINGS_TESTIMONIAL,
  GET_WEBSITE_SETTINGS,
  getWebsiteSettings,
  SET_APP_CONFIG,
  SET_APP_CONFIG_LANGUAGES,
  SET_WEBSITE_SETTINGS,
  setWebsiteSettings,
  UPDATE_WEBSITE_SETTINGS_TESTIMONIAL,
  UPLOAD_APP_CONFIG_LOGO,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  uploadWebsiteSettingsImage,
} from '../../WebsiteAndApp/actions';
import EditWebsiteSettingsImagesPanel from '../../WebsiteAndApp/Edit/EditWebsiteSettingsImagesPanel';
import EditWebsiteSettingSkeleton from '../../WebsiteAndApp/Edit/EditWebsiteSettingSkeleton';
import { ImageLocationEnum } from '../../WebsiteAndApp/service';

const loadingSelector = createLoadingSelector([GET_WEBSITE_SETTINGS]);
const savingSelector = createLoadingSelector([
  SET_APP_CONFIG,
  SET_APP_CONFIG_LANGUAGES,
  UPLOAD_APP_CONFIG_LOGO,
  SET_WEBSITE_SETTINGS,
  UPDATE_WEBSITE_SETTINGS_TESTIMONIAL,
  ADD_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_TESTIMONIAL,
  DELETE_WEBSITE_SETTINGS_IMAGE,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
]);
const errorSelector = createLoadingErrorSelectorFactory([
  SET_APP_CONFIG,
  SET_APP_CONFIG_LANGUAGES,
  UPLOAD_APP_CONFIG_LOGO,
]);

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    marginLeft: '-4px',
    marginTop: '-12px',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    width: 'calc(100% + 20px)',
    height: '100%',
    overflowY: 'scroll',
  },
}));

type FormValues = ReturnType<typeof mapPropsToValues>;

const mapPropsToValues = (props: Props) => {
  const { settings } = props;

  return settings;
};

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const mapStateToProps = (state: AppState) => {
  const {
    settings: { Images = [], ...settings },
  } = state.website;

  return {
    images: processImages(Images),
    settings,
    translate: getTranslate(state.locale),
    loading: loadingSelector(state),
    saving: savingSelector(state),
    currentApp: getSelectedApp(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  setSettings: (settings: IndexPageBase) => dispatch(setWebsiteSettings(settings)),
  getSettings: () => dispatch(getWebsiteSettings()),
  uploadImage: (imageLocation: WebsiteImage.ImageLocationEnum, file) =>
    dispatch(uploadWebsiteSettingsImage(imageLocation, file)),
});

export default compose<Props, {}>(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik<Props, FormValues>({
    displayName: 'KioskHomeScreenCustomization',
    enableReinitialize: true,
    mapPropsToValues,
    handleSubmit: (values: FormValues, formikBag) => {
      const {
        props: { settings, setSettings },
      } = formikBag;

      if (!isEqual(values, settings)) {
        setSettings(values);
      }
    },
  })
)((props: Props & FormikProps<FormValues>) => {
  const {
    translate,
    images,
    getSettings,
    submitForm,
    values,
    dirty,
    loading,
    saving,
    error,
    currentApp,
  } = props;
  const isMounted = useIsMounted();
  const classes = useStyles(props);

  const submit = useMemo(
    () =>
      debounce(() => {
        if (isMounted()) {
          submitForm();
        }
      }, 3000),
    []
  );

  // @ts-ignore
  const { success, failure } = useRequestSnackBarIndicators(saving, error);

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    if (dirty && values) {
      submit();
    }
  }, [dirty, values]);

  useRequestSnackBar({
    loading,
    success,
    failure,
    failureMessage: String(error),
    autoSaving: saving,
  });

  return (
    <PageLayout
      title={translate('Kiosks_Home_Screen_Images')}
      toParent={`/${currentApp.AppId}/kiosks`}
      fullWidth
      strictToParent
      documentTitle="Kiosks"
    >
      <div className={classes.wrapper}>
        <div className={classes.content}>
          {loading ? (
            <EditWebsiteSettingSkeleton />
          ) : (
            <EditWebsiteSettingsImagesPanel
              isExpandable
              className=""
              name=""
              expanded={true}
              onExpand={() => {}}
              onToggle={() => {}}
              images={images[ImageLocationEnum.IndexHeader]}
              imageLocation={WebsiteImage.ImageLocationEnum.IndexHeader}
              fdKeyName="carousel"
              title="These_images_change_to_the_next_image_every_15_sec"
              maxImages={6}
              minImages={1}
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
});
